import { type WabunkaError, WabunkaError_Source } from '@japan-culture-and-technology/wabunka-protos/ts_proto/wabunka_error';

import type { UseFetchFromPublicWabunkaApiArgs } from '@/composables/useFetchFromPublicWabunkaApi';
import { useFetchKey, useRuntimeConfigKey, type UseFetch, type UseRuntimeConfig } from '@/injectionKeys';
import { WabunkaAPIError, isWabunkaError } from '@/models/error';
import { useSentry } from '@/wabunka-src/common/composables/general/useSentry/useSentry';
import { buildFetchKey } from '@/wabunka-src/common/utils/buildFetchKey';

/**
 * ヘッダーの組み立てをおこなう
 * @returns ヘッダー用オブジェクト
 */
const buildHeader = () => {
  return {
    // eslint-disable-next-line
    'Content-Type': 'application/json',
    Accept: 'application/json',
    // eslint-disable-next-line
    'X-Wabunka-Language': 'en',
  };
};

/**
 * Public Wabunka API からデータを取得する useFetch ラッパー Composable
 * ページ層では直接使用せず、ドメイン知識を有した Composable を介して利用してください
 *
 * @param UseFetchFromPublicWabunkaApiArgs
 * @return data と status
 */
export const useAsyncFetchFromPublicWabunkaApi = async <T>({ path, query, method, body, options, converter, fetchKey, throwOnError = true }: UseFetchFromPublicWabunkaApiArgs<T>) => {
  const useRuntimeConfig = inject(useRuntimeConfigKey) as UseRuntimeConfig;
  const runtimeConfig = useRuntimeConfig();
  const useFetch = inject(useFetchKey) as UseFetch;
  const { captureWabunkaException, captureWabunkaWarning } = useSentry();

  // Nuxt 4 では useFetch が同じ key のときにキャッシュを共有するため、
  // リクエストを区別する入力をすべて key に含める。とくに body を落とすと、同じ path への
  // 別内容の POST が 1 回目の応答をそのまま返す。
  // fetchKey が指定された場合はそれを最優先（呼び出し側が完全に key を制御したい特殊ケース）。
  const { customKey, ...restOptions } = options ?? {};
  const key = fetchKey ?? buildFetchKey(path.value, query?.value, customKey, { method, body: body?.value });

  const { data, error, status, refresh } = await useFetch(path, {
    key,
    ...restOptions,
    query,
    ...(method !== undefined ? { method } : {}),
    ...(body !== undefined ? { body } : {}),
    baseURL: runtimeConfig.public.WABUNKA_PUBLIC_API_BASE_URL as string,
    onRequest({ options }) {
      options.headers = new Headers(buildHeader());
    },
  });

  if (error.value != null) {
    const fetchError = error.value;
    const errorData = fetchError.data;

    // APIからの正当なエラーレスポンス（WabunkaError形式）であるか確認
    if (isWabunkaError(errorData)) {
      if (throwOnError) {
        throw new WabunkaAPIError({
          ...errorData,
          status: Number(errorData.status),
          source: errorData.source ?? WabunkaError_Source.WABUNKA,
        });
      }
    } else {
      const statusCode = fetchError.statusCode ?? 500;
      const message = fetchError.message || `Unexpected error occurred for ${path.value}`;

      // Sentryに元のエラーメッセージを詳細に送信する
      // ユーザーのネットワーク起因（no response / TypeError: Load failed）は warning に落とす
      const sentryMessage = `Fetch Error [${statusCode}]: "${message}" for ${path.value}`;
      const isNetworkError = message.includes('<no response>') || message.includes('TypeError: Load failed');
      if (isNetworkError) {
        captureWabunkaWarning(sentryMessage);
      } else {
        captureWabunkaException(new Error(sentryMessage));
      }

      if (throwOnError) {
        throw new WabunkaAPIError({
          status: statusCode,
          message: `Unexpected error: ${message}`,
          source: WabunkaError_Source.WABUNKA,
        } as WabunkaError);
      }
    }
  }

  // 「エラーなしで data が null」の異常応答を検知して Sentry に警告を送る（JCAT-5203）。
  // Nuxt 4 アップグレード時にこの状態が発生し、公開ページが 200 + noindex で配信されて
  // インデックスから除外される事故が起きた。同種の問題の早期検知のための計装。
  if (status.value === 'success' && error.value == null && data.value == null) {
    captureWabunkaWarning(`[JCAT-5203] API response was null without error: ${path.value}`);
  }

  const convertedData = computed<T | null>(() => {
    if (data.value == null) {
      return null;
    }
    return converter(data.value);
  });

  return { data: convertedData, status, refresh };
};
